import { publicContentOptions, protectedContentOptions } from '@/api/query-options';
import { lazy, Suspense } from 'react';
import { ConfirmationHost } from '@/components/confirmation-host';
import { PageLoading } from '@/components/page-loading';
import { ApplicationBoundary } from '@/components/application-boundary';
import { RouteNavigation } from '@/components/route-navigation';
const AccountPage = lazy(() =>
  import('@/donor/account.page').then((module) => ({ default: module.AccountPage })),
);
const BasketPage = lazy(() =>
  import('@/donor/basket.page').then((module) => ({ default: module.BasketPage })),
);
const GiftPage = lazy(() =>
  import('@/donor/gift.page').then((module) => ({ default: module.GiftPage })),
);
import { GivingProvider } from '@/donor/giving-provider';
import { DonorLoadingPage, MissingDonorPage } from '@/donor/ui';
import { DonorRuntime } from '@/donor/runtime';
import { AccountLanguageProvider } from '@/donor/account-language-provider';
import { MobileNavigationLayer } from '@/donor/mobile-navigation';
const SignInPage = lazy(() =>
  import('@/donor/sign-in.page').then((module) => ({ default: module.SignInPage })),
);
const CheckoutPage = lazy(() =>
  import('@/donor/checkout.page').then((module) => ({ default: module.CheckoutPage })),
);
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LanguageProvider } from '@/i18n/language-provider';
import { PublicAnalytics } from '@/components/public-analytics';

import { AppFallbackPage } from '@/pages/app-fallback.page';
import { DeleteAccountPage } from '@/pages/delete-account.page';
import { HomePage } from '@/pages/home.page';
const PrivacyPage = lazy(() =>
  import('@/pages/privacy.page').then((module) => ({ default: module.PrivacyPage })),
);
const ProjectPage = lazy(() =>
  import('@/pages/project.page').then((module) => ({ default: module.ProjectPage })),
);
const OrganizationPage = lazy(() =>
  import('@/pages/organization.page').then((module) => ({ default: module.OrganizationPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: publicContentOptions,
  },
});
queryClient.setQueryDefaults(['donor'], protectedContentOptions);

export const AppRoutes = (): JSX.Element => (
  <AccountLanguageProvider>
    <PublicAnalytics />
    <DonorRuntime />
    <RouteNavigation />
    <ApplicationBoundary>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/p/:slug" element={<ProjectPage />} />
          <Route path="/o/:organizationId" element={<OrganizationPage />} />
          <Route path="/organizations/:organizationId/projects/:projectId" element={<GiftPage />} />
          <Route path="/give/:organizationId/:projectId" element={<GiftPage />} />
          <Route path="/give/fund/:orgRef/:fund" element={<GiftPage />} />
          <Route path="/basket" element={<BasketPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/auth/callback" element={<SignInPage callback />} />
          <Route
            path="/checkout/:id"
            element={
              <Suspense fallback={<DonorLoadingPage />}>
                <CheckoutPage />
              </Suspense>
            }
          />
          <Route path="/account" element={<Navigate to="/account/donations" replace />} />
          <Route path="/account/:section" element={<AccountPage />} />
          <Route path="/me/data-exports/:id" element={<AccountPage exportLink />} />
          <Route path="/checkout/*" element={<MissingDonorPage />} />
          <Route path="/auth/*" element={<MissingDonorPage />} />
          <Route path="/me/*" element={<MissingDonorPage />} />
          <Route path="/app" element={<AppFallbackPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ApplicationBoundary>
    <MobileNavigationLayer />
  </AccountLanguageProvider>
);

export const App = (): JSX.Element => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <ApplicationBoundary>
        <ConfirmationHost>
          <GivingProvider>
            <AppRoutes />
          </GivingProvider>
        </ConfirmationHost>
      </ApplicationBoundary>
    </LanguageProvider>
  </QueryClientProvider>
);
